import type { IssueWithAssetRegister } from "kubb";
import React from "react";
import DataTable from "react-data-table-component";
import { AssetTypeToHumanString } from "../../../components/Asset/AssetTypeHumanLabel.tsx";
import BlockSpinner from "../../../components/Spinners/BlockSpinner.tsx";
import CopyToClipboard from "../../../components/utils/CopyToClipboard.tsx";
import { formatDate } from "../../../components/utils/HighlightNullAndOldDates.tsx";
import { issueCategoryFormatter } from "./model.tsx";

interface IssueSelectComponentProps {
  items: IssueWithAssetRegister[];
  loading: boolean;
  setValues: (values: IssueWithAssetRegister[]) => void;
}

export default function IssueSelectComponent(
  props: IssueSelectComponentProps,
): React.ReactElement {
  const onSelect = (selected: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: IssueWithAssetRegister[];
  }) => {
    props.setValues(selected.selectedRows);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: want to trigger when items change
  React.useEffect(() => {
    props.setValues([]);
  }, [props.items, props.setValues]);

  const BlankTable = (
    <BlockSpinner loading={true}>
      <div style={{ height: "300px" }}>
        <DataTable
          columns={[
            {
              name: "Address",
              selector: () => "...",
              wrap: true,
            },

            {
              name: "Group",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Location Ref",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Asset Position",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Asset Type",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Issue",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Start Date",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "End Date",
              selector: () => "...",
              wrap: true,
            },
          ]}
          data={[{}, {}, {}]}
          selectableRows={true}
          dense={true}
          fixedHeader={true}
          fixedHeaderScrollHeight={"300px"}
        />
      </div>
    </BlockSpinner>
  );

  return (
    <>
      <DataTable
        progressPending={props.loading}
        progressComponent={BlankTable}
        columns={[
          {
            name: "Address",
            selector: (row: IssueWithAssetRegister) =>
              row.location_address || "",
            wrap: true,
          },

          {
            name: "Group",
            selector: (row: IssueWithAssetRegister) => row.group || "",
            wrap: true,
          },
          {
            name: "Location Ref",
            selector: (row: IssueWithAssetRegister) =>
              row.location_reference || row.location_id,
            cell: (row: IssueWithAssetRegister) =>
              row.location_reference ? (
                <>{row.location_reference}</>
              ) : (
                <CopyToClipboard value={row.location_id} slice={8} />
              ),

            wrap: true,
          },
          {
            name: "Asset Position",
            selector: (row: IssueWithAssetRegister) =>
              row.asset_position_reference ||
              `...${row.asset_position_id.slice(0, 8)}`,
            cell: (row: IssueWithAssetRegister) =>
              row.asset_position_reference ? (
                <>{row.asset_position_reference}</>
              ) : (
                <CopyToClipboard value={row.asset_position_id} slice={8} />
              ),
            wrap: true,
          },
          {
            name: "Asset Type",
            selector: (row: IssueWithAssetRegister) =>
              AssetTypeToHumanString(row.asset_type),
            wrap: true,
          },
          {
            name: "Issue",
            selector: (row: IssueWithAssetRegister) =>
              issueCategoryFormatter(row),
            wrap: true,
          },
          {
            name: "Start Date",
            selector: (row: IssueWithAssetRegister) =>
              formatDate(row.start_date_time, "L LTS"),
            wrap: true,
          },
          {
            name: "End Date",
            selector: (row: IssueWithAssetRegister) =>
              row.end_date_time
                ? formatDate(row.end_date_time, "L LTS")
                : "Ongoing",
            wrap: true,
          },
        ]}
        data={props.items}
        selectableRows={true}
        onSelectedRowsChange={onSelect}
        dense={true}
        fixedHeader={true}
        fixedHeaderScrollHeight={"300px"}
      />
    </>
  );
}
